/* ============================================================================
 * oscCtrl.js — Continuous OSC Control Lanes
 * ============================================================================
 */

import { createAnimationLoop } from "./cueAnimLoop.js";
import { createOscOverlay } from "./osc.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { publish } from '../control/paramBinding.js';
import { playheadScreenX } from "../system/paths.js";
import { laneTable, pointAtX } from "./laneValue.js";

import {
    registerAnimation,
    registerRunningAnimation,
    clearRunningAnimation,
    resolveAnimationUid
} from "./animation.js";


const OSCCTRL_THROTTLE_MS = 30;

export function handleOscCtrlCue(el, args = []) {
    const cfg = {
        addr: null,
        min: 0,
        max: 1,
        trig: "auto",
        mode: "event",
        uid: resolveAnimationUid(el, "oscCtrl", args),

        _path: null,
        _bbox: null,
        _len: 0,
        _lastSent: 0,

        _lastValue: null,
        _epsilon: 0.005,

        _overlay: null
    };

    for (const a of args) {
        switch (a.type) {
            case "addr": cfg.addr = String(a.value); break;
            case "min": cfg.min = Number(a.value); break;
            case "max": cfg.max = Number(a.value); break;
            case "uid": cfg.uid = String(a.value); break;
            case "mode": cfg.mode = String(a.value).toLowerCase(); break;
        }
    }

    if (!cfg.addr) {
        console.warn("[oscCtrl] missing addr — skipping");
        return;
    }

    if (el.tagName?.toLowerCase() !== "path") {
        console.warn("[oscCtrl] cue must be attached to a <path>");
        return;
    }

    cfg._path = el;
    cfg._bbox = el.getBBox();
    cfg._len = el.getTotalLength();
    cfg._table = laneTable(el);          // x along the path, for pointAtX

    // ------------------------------------------------------
    // Debug overlay (global overlay system)
    // ------------------------------------------------------
    cfg._overlay = createOscOverlay({
        anchorEl: el,                 // overlay glued to path bbox
        label: cfg.addr || "oscCtrl"  // label (or addr)
    });

    // initial display (the address)
    cfg._overlay.update(`${cfg.min} → ${cfg.max}`);
    cfg._overlay.position();


    registerAnimation(el, "oscCtrl", cfg, () => startOscCtrl(cfg));

    // Lanes are passive: nothing triggers them, the per-frame tick decides
    // whether the playhead is inside the path. So start now (a re-scan of
    // the same score replaces the previous instance).
    window.runningAnimations?.get(cfg.uid)?.stop?.();
    startOscCtrl(cfg);
}



function startOscCtrl(cfg) {
    let loop = null;

    const instance = {
        uid: cfg.uid,
        stop() {
            loop?.stop();
            loop = null;
            cfg._anim = null;
            clearRunningAnimation(cfg.uid);
            if (cfg._overlay) {
                cfg._overlay.destroy();
                cfg._overlay = null;
            }
        },
        tick() {
            // the playhead on the screen (playheadScreenX is from the score
            // container's left edge; the lane is measured on the screen)
            const fromLeft = playheadScreenX();
            if (fromLeft == null) return;
            const playX = fromLeft + (window.scoreContainer?.getBoundingClientRect().left || 0);

            const rect = cfg._path.getBoundingClientRect();
            const EPS = 1.5;

            // overlay ALWAYS follows, even when not active
            if (cfg._overlay) cfg._overlay.position();

            // determine whether we are inside path horizontally
            const inside =
                !(playX < rect.left - EPS || playX > rect.right + EPS);

            if (!inside) {
                // do nothing else — just keep HUD locked
                return;
            }

            // The playhead in the path's own units, and the point of the
            // path at that x (laneValue.js) -- not the point at the same
            // fraction of its length, which a steep stretch puts out of step.
            const ctm = cfg._path.getScreenCTM();
            if (!ctm) return;
            const ux = new DOMPoint(playX, rect.top + rect.height / 2).matrixTransform(ctm.inverse()).x;
            const p = pointAtX(cfg._table, cfg._path, ux);

            // how far through the lane, 0..1
            let t = cfg._bbox.width > 0 ? (ux - cfg._bbox.x) / cfg._bbox.width : 0;
            t = Math.min(1, Math.max(0, t));

            // normalize Y
            let ny = (p.y - cfg._bbox.y) / cfg._bbox.height;
            ny = Math.min(1, Math.max(0, 1 - ny));

            const value = cfg.min + ny * (cfg.max - cfg.min);

            // event mode (the default): only send when the value has moved
            // by more than epsilon since the last value actually SENT.
            // Epsilon is relative to the lane's range, so a 0..0.8 lane is
            // as sensitive as a 200..4000 one. (Against the previous
            // frame's value, a slowly moving playhead never accumulates
            // enough change and stays silent.)
            if (cfg.mode === "event" && cfg._lastValue !== null) {
                const eps = cfg._epsilon * (Math.abs(cfg.max - cfg.min) || 1);
                if (Math.abs(value - cfg._lastValue) < eps) return;
            }

            const now = performance.now();
            if (now - cfg._lastSent < OSCCTRL_THROTTLE_MS) return;

            cfg._lastSent = now;
            cfg._lastValue = value;

            sendOSC({
                type: "osc_control",
                addr: cfg.addr,
                uid: cfg.uid,
                value,
                t,
                timestamp: Date.now()
            });

            // Control plane publish (ny is the 0-1 normalized Y)
            publish("oscCtrl", cfg.uid, { t, v: ny });
        }
    };

    registerRunningAnimation(cfg.uid, instance);

    // Drive tick() from the shared animation coordinator (runs from the
    // background clock too, so lanes keep streaming behind other windows).
    // Exposed as cfg._anim so the visibility observer can pause an
    // offscreen lane and resume it when it scrolls back into view.
    loop = createAnimationLoop({
        dur: 1,
        loop: 0,
        onTick: () => instance.tick()
    });
    cfg._anim = loop;
    loop.start();
}
